// src/pages/Contact.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import AnimatedSocialLinks from '../components/AnimatedSocialLinks';
import DecryptedText from '../components/DecryptedText';
import ShinyText from '../components/ShinyText';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { captureInstallPrompt, triggerInstallPrompt, initializeFirebaseMessaging } from '../serviceWorkerRegistration';
import { Meteors } from '../components/ui/meteors';
import GlitchText from '../components/GlitchText';
import './Home.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_API_URL;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { darkMode, toggleDarkMode } = useTheme();
  const { user, openLogin, userLoading, isAuthLoading, handleLogout } = useAuth();
  const [canInstall, setCanInstall] = useState(false);
  const navigate = useNavigate();
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/contact/`, formData, {
        withCredentials: true,
      });
      setStatus('Message sent successfully!');
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setStatus('Message sending failed. Please try again.');
      toast.error('Message sending failed. Please try again.');
      console.error('Contact failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      {/* ───── BACKGROUND BLOBS ───── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* ───── SEO ───── */}
      <Helmet>
        <title>Contact SErraND | Find Trusted Service Providers in Kenya</title>
        <meta
          name="description"
          content="Contact SErraND for help finding trusted service providers in Kenya. Reach out for support, service inquiries, provider issues, or to connect with verified fundis and professionals."
        />
        <meta
          name="keywords"
          content="SErraND contact, find service providers Kenya, trusted fundis Kenya, hire plumber electrician tutor Kenya, reliable home services Kenya, service provider support, book local services Kenya, connect with service professionals"
        />
        <meta name="author" content="Marvin Useki" />
        <link rel="canonical" href="https://www.serrand.org/contact/" />
        <meta property="og:title" content="Contact SErraND | Trusted Services in Kenya" />
        <meta
          property="og:description"
          content="Need help finding a reliable service provider in Kenya? Contact SErraND for support, inquiries, or assistance connecting with verified professionals."
        />
        <meta property="og:url" content="https://www.serrand.org/contact/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.serrand.org/images/contact-image.jpg" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'SErraND',
            url: 'https://www.serrand.org',
            logo: 'https://www.serrand.org/logo512.png',
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '+254-740-879229',
              contactType: 'Customer Support',
              areaServed: 'KE',
              availableLanguage: 'English',
            },
            description:
              'SErraND connects clients with trusted and verified service providers across Kenya.',
          })}
        </script>
      </Helmet>

   <Meteors count={40} angle={220} />

   <Navbar
     user={user}
     userLoading={userLoading}
     isAuthLoading={isAuthLoading}
     handleLogout={handleLogout}
     openLogin={() => navigate('/', { state: { showLogin: true } })}
     openRegister={() => navigate('/', { state: { showRegister: true } })}
     toggleDarkMode={toggleDarkMode}
     darkMode={darkMode}
   />


{/* About SErraND Section */}
      <section className="relative py-20 px-4 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-4">
            About SErraND
          </h1>
          <p className="text-2xl text-muted-foreground font-medium">Plug Wa Kazi</p>
        </div>

        <Card className="max-w-5xl mx-auto border-border">
          <CardContent className="p-8 md:p-12">
            <div className="prose prose-neutral dark:prose-invert max-w-none space-y-6 text-foreground/90">
              <p className="text-lg leading-relaxed">
                SErraND exists because finding reliable help in Kenya should not feel like gambling. 
                If you’ve ever needed a plumber, electrician, tutor, cleaner, or fundi and had no idea who to trust, 
                you already understand the problem.
              </p>
              <p className="text-lg leading-relaxed">
                I’m <strong>Marvin Useki</strong>, based in Kitengela, Kajiado County. When I moved here, 
                I ran straight into the same wall most people hit: no trusted contacts, no verified professionals, 
                just guesswork and crossed fingers.
              </p>
              <p className="text-lg leading-relaxed">
                SErraND connects clients with vetted service providers across Kenya. Clear profiles, 
                accountability, and transparency are non-negotiable. Whether you need urgent help or 
                are planning a big project, SErraND helps you start with confidence.
              </p>
            </div>

            {/* Support Box */}
            <div className="mt-10 p-8 bg-muted/50 rounded-2xl border border-border">
              <h3 className="text-2xl font-semibold mb-4">Support SErraND</h3>
              <div className="space-y-3 text-sm">
                <p>
                  <span className="font-medium">M-Pesa:</span>{' '}
                  <a href="tel:+254740879229" className="text-primary hover:underline">
                    0740 879 229
                  </a>
                </p>
                <p>
                  <span className="font-medium">Bank Paybill:</span> 625625, Account 20138250
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Contact Form Section */}
      <section className="relative py-16 px-4 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Need help finding a service provider, reporting an issue, or offering your services? 
            Reach out and we’ll point you in the right direction.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Send us a message</CardTitle>
              <CardDescription>
                We typically respond within 24 hours during business days.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="e.g. Need a plumber in Kitengela"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Briefly describe what you need help with..."
                    rows={6}
                    required
                  />
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ───── FOOTER ───── */}  
  <Footer
    canInstall={canInstall}
    triggerInstallPrompt={triggerInstallPrompt}
  />
</div>

);
}
